import { BLEND_MODES, InteractionEvent } from 'pixi.js';
import { RoadRage } from "../../RoadRage";
import { Helper } from "../../../dev/utils/Helper";
import { RoadRageSymbolData, SymbolGfxColor } from './RoadRageSymbolData';
import { MiniPayTable } from "../../../dev/extra/MiniPayTable";
import { Power4, Bounce, Power3, Power2, Back, gsap } from 'gsap';
import { TextStyleOptions } from "../../../dev/setting/text/IText";
import { SymbolData } from "../../../dev/reel/symbol/config/SymbolData";
import { ReelSpriteSymbol } from "../../../dev/reel/symbol/ReelSpriteSymbol";
import { ReelSymbolStates } from "../../../dev/reel/symbol/ReelSpriteSymbolArea";
import { ISymbolAnimationConf } from "../../../dev/reel/symbol/ReelBaseSymbol";
import { ISymbolConfig } from "../../../dev/reel/symbol/config/IReelSymbolStruct";
import { FontStyle, FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { RoadRageWinSymbol } from "./RoadRageWinSymbol";
import { RoadRageScatterLamps, ScatterSymState } from './RoadRageScatterLamps';
import { AnimationEffects } from '../../../dev/effects/AnimationEffects';
import { MathHelper } from '../../../dev/utils/MathHelper';
import { RoadRageSymbolMultiplier } from './RoadRageSymbolMultiplier';
import { ArrayHelper } from '../../../dev/utils/ArrayHelper';
import { SoundManager } from '../../config/sound/SoundManager';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { SpinePlayer } from '../../../dev/displayobject/SpinePlayer';
import { OverlayItem, OverlayAnimation } from '../../independent/animations/OverlayAnimation';

export class RoadRageSpriteSymbol extends ReelSpriteSymbol {

    private readonly _styleWays: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontStyle: FontStyle.ITALIC,
        fontSize: 64,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 8,
        dropShadowDistance: 8,
        fill: "#FFDD02",
        lineJoin: "round",
        align: "center",
        stroke: "#000000",
        strokeThickness: 8
    };

    private nrOnMult: number = 1;
    private flashGFX: PIXI.Graphics;
    private multiplier: RoadRageSymbolMultiplier;
    private winSym: RoadRageWinSymbol;

    private scatterLamps: RoadRageScatterLamps;
    private scatterBg: PIXI.Sprite;
    private isSetupMarked: boolean = false;

    private missingtSymType: PIXI.Text;

    private CarOverlayDisp: PIXI.DisplayObject;
    private CarOverlayTrail: PIXI.DisplayObject;

    public get gotMarked(): boolean {
        return this.isSetupMarked;
    }

    public get isCar(): boolean {
        return this.symName.startsWith("WC", 0);
    }

    public get isSetupFeature(): boolean {
        return this.symName[0] === "F";
    }

    public get symbolHeight(): number {
        return this.sizeType.value;
    }

    public get symbolWidht(): number {
        return RoadRageSymbolData.DEFAULT_DIM.WIDTH;
    }

    public get symbolSizeY(): number {
        return (this.symbolHeight * this.scale.y);
    }

    public get symbolSizeX(): number {
        return (this.symbolWidht * this.scale.x);
    }

    public get splitValue(): number {
        return this.nrOnMult;
    }

    private get missingTexture(): boolean {
        return !this.symbolConfig || !RoadRageSymbolData.getTextures(this.symName, this.state.value, this.sizeType.value);
    }

    private get multPosSize(): number{
        return this.sizeType.equal(144) ? -10 : this.sizeType.equal(108) ? -20 : -15;
    }

    private get multPos(): PIXI.Point {
        return new PIXI.Point(this.symbolWidht*-0.25, this.isCar ? this.multPosSize : 0);
    }

    constructor(ID: number, symName: string, state: string = ReelSymbolStates.NORMAL, type?: number) {
        super(ID, symName, state, type);

        this.addMissingText(symName);

        if (this.isScatter) {
            this.scatterLamps = this.addChild(new RoadRageScatterLamps(this.posID[0], this.state.value, this.sizeType.value));
            this.scatterLamps.zIndex = 5;
            if (type !== 108) {
                this.scatterBg = this.addChild(new PIXI.Sprite(RoadRage.assetLoader.getTextures("S/" + type + "/S_bg")[0]))
                this.scatterBg.anchor.set(0.5, 0.5);
                this.scatterBg.zIndex = -100;
            }
            this.sortChildren();
        }
        if (this.isCar) {
            if (this.symName === "WC2") this.scale.x = -1;
            this.scale = MathHelper.pointMul(this.scale, 0.85);
        }
    }

    public setSymbol(symName: string): void {
        if(RoadRage.apiPlugin.gameClientConfiguration.useCensoredGfxUS){
            if(symName == "M3"){
                symName = "M3_WV";
            }
            if(symName == "L3"){
                symName = "L3_WV";
            }
        }
        super.setSymbol(symName);
    }

    public scatterState(state: any, instant?: boolean): gsap.core.Timeline {
        if (!this.isScatter) return new gsap.core.Timeline();

        const tl: gsap.core.Timeline = this.scatterLamps.changeState(state, instant)!;
        if (state === ScatterSymState.LEVEL_4 || state === ScatterSymState.LEVEL_5) {
            const startPos: PIXI.Point = MathHelper.pointAdd(this.position, 0);
            tl.add(
                AnimationEffects.Shake({
                    x: 0.5,
                    y: 1.5,
                    obj: this,
                    hertz: 80,
                    duration: instant ? 0.1 : 1,
                    Intensity: 2.75,
                    updateOnComplete: true,
                    positionCb: () => startPos,
                    ease: Power2.easeOut
                }).play(), 0
            );
        }
        return tl;
    }

    private addMissingText(symName: string): void {
        if (this.missingTexture) {
            if (!this.missingtSymType) {
                this.missingtSymType = this.addChild(new PIXI.Text(symName, this._styleWays))
                this.missingtSymType.anchor.set(0.5, 0.5);
                this.missingtSymType.zIndex = 10;
                this.sortChildren();
            }
            else
                this.missingtSymType.text = symName;
        }
    }

    protected Init(): void {
        super.Init();
        this.createFlashGraphic();
    }

    public changeState(stateConfig: ISymbolAnimationConf): gsap.core.Timeline {

        if (stateConfig.sym! && !SymbolData.getTextures(stateConfig.sym!, stateConfig.state, this.sizeType.value)) {
            this.setSymbol(stateConfig.sym!);
            this.addMissingText(stateConfig.sym!);

            if (this.mainSp) {
                this.mainSp.destroy();
                this._mainSp = undefined!;
            }

            if (this.subSp) {
                this.subSp.destroy();
                this._subSp = undefined!;
            }

            return new gsap.core.Timeline();
        }
        if (stateConfig.state === ReelSymbolStates.SPIN) {

            if (this.swearItem && this.swearItem.timeline.isActive()) {
                this.swearItem.timeline.progress(1);
            }

            if (this.multiplier) {
                this.addChild(this.multiplier);
                this.multiplier.position = this.multPos;
                this.sortChildren();
            }

            if (this.isMystery || (this.isCar && this.state.equal("Wild") || this.state.equal("Sticky"))) {
                stateConfig.state = this.state.value;
            }
        }

        if (stateConfig.state === ReelSymbolStates.NORMAL && this.splitValue > 1) stateConfig.state = this.splitValue.toString();
        const tl: gsap.core.Timeline = super.changeState(stateConfig);

        if (this.symName == "W") {
            this.scale.set(1, 1);
            this.mainSp.scale.x = 1;
        }

        if (stateConfig.state === "Disabled" && this.multiplier) {
            this.multiplier.shadowAlpha(true, 0.5, stateConfig.duration);
        }

        return tl;
    }

    public playWildSwitch(stateConfig: ISymbolAnimationConf): gsap.core.Timeline {

        this.state.set(stateConfig.state);
        this.setSymbol(stateConfig.sym!);
        

        const animationName: string = "Pop_WC_" 
        + stateConfig.sym!.slice(2)
        + "_" + this.sizeType.value;

        const animPlayer = RoadRage.overlayAnim.playSpine(this, "WILD", {
            animation: animationName, onStart: () => {
                this.visible = true;
                if(this.spineWild) this.spineWild.stop(true);
            }, onComplete: () => {
                if (this.multiplier) {
                    RoadRage.overlayAnim.addChild(this.multiplier);
                    this.multiplier.position = Helper.copyLocalPosition(this.multiplier, this);
                    this.multiplier.position.x += this.multPos.x;
                    this.multiplier.position.y += this.multPos.y;
                    animPlayer.timeline.add(() => {
                        this.addChild(this.multiplier);
                        this.multiplier.position = this.multPos;
                        this.sortChildren();
                    });
                }
            }
        }, false);

        this.sortChildren();

        return animPlayer.timeline;
    }

    public setNonAlpha(alpha: number = 0.55): void {
        if (this.isScatter) return;
        this._darkSquareGfx.alpha = alpha;
        if (this.multiplier) this.multiplier.setShadowAlpha(alpha);
    }

    public markNonWin(enable: boolean, alpha: number = 0.55, durr: number = 0.25): void {
        if (this.isScatter || this.isMystery) {
            super.markNonWin(false, alpha, durr)
            if (this.multiplier) this.multiplier.shadowAlpha(false, alpha, durr);
        } else {
            super.markNonWin(enable, alpha, durr);
            if (this.multiplier) this.multiplier.shadowAlpha(enable, alpha, durr);
        }
    }

    public setInteractive(val: boolean): void {
        if (this.isWild || this.isScatter) return;

        if (this.mainSp) {
            this.mainSp!.interactive = val;
            this.mainSp!.on('pointerdown', () => {
                MiniPayTable.instance.onClicked({
                    dispObj: this.mainSp,
                    name: this.symName
                })
            });
        }

    }

    public addSumMult(val: number, type?: string, spec?: boolean): void {
        this.nrOnMult += val;
        this.applyMultValue(this.nrOnMult, undefined, spec)
        this.playFlash();

        if (this.multiplier) {
            this.addChild(this.multiplier);
            this.multiplier.position = this.multPos;
            this.sortChildren();
        }
    }

    public addMult(val: number, type?: string, animated?: boolean, playSound:boolean = true): gsap.core.Timeline {
        if (!val) return new gsap.core.Timeline();

        if (animated) {
            return RoadRage.overlayAnim.playPlusOneText(this, val, type).add(() => {
                
                if(playSound){
                    this.playMultSound();
                } 

                this.nrOnMult += val;
                
                this.applyMultValue(this.nrOnMult);

                this.playFlash();

            })
        } else {
            this.nrOnMult += val;
            this.applyMultValue(this.nrOnMult)
            this.playFlash();
            return new gsap.core.Timeline();
        }
    }

    private playMultSound():void{
        if(this.nrOnMult == 1){
            SoundManager.playSoundEffect(SoundConfig.MULT_LAND)
        }else{
            SoundManager.playSoundEffect(SoundConfig.MULT_LAND_UPGRADE)
        }
    }

    protected createFlashGraphic(): void {
        this.flashGFX = new PIXI.Graphics();
        this.flashGFX.alpha = 0;
        this.flashGFX.zIndex = 290;
        this.addChild(this.flashGFX);
        this.sortChildren();
    }

    glowPreHit(hits: boolean, duration: number = 0.25, winLvl: number = 0) {
        if (hits) {
            if (this.useWinSymbol()) {
                this.winSym.Glow(duration, winLvl, this.sizeType.value / RoadRageSymbolData.DEFAULT_DIM.HEIGHT);
            }
        }
    }

    hintValue(val:number,ways:number) {
        RoadRage.overlayAnim.playValueText(this,val,ways);
    }

    playBigWin(hits: boolean, durr: number): void {
        if (hits) {
            this.useWinSymbol();
            this.winSym.BigWinStart(durr);
        }
        this.markNonWin(!hits, 0.6, durr);
    }

    playWin(hits: boolean, duration: number, winLvl: number = 0) {
        if (this.isScatter) return;
        if (hits) {
            if (this.useWinSymbol()) {
                this.winSym.Play(winLvl, duration);
                if (this.multiplier) {
                    RoadRage.overlayAnim.addChild(this.multiplier);
                    RoadRage.overlayAnim.sortChildren();
                    this.multiplier.playWin()
                }
            }
        }
        this.markNonWin(!hits, undefined, duration);
    }

    private useWinSymbol(shouldBeCreated: boolean = true): boolean {
        if (!this.winSym && this.mainSp && shouldBeCreated) {
            this.winSym = new RoadRageWinSymbol(this.posID[0], "normal", this.sizeType.value, this.isCar ? this.mainSp.texture : RoadRageSymbolData.getTextures(this.symName, "normal", this.sizeType.value)![0]);
        }

        if (this.winSym) {
            this.winSym.position = Helper.copyLocalPosition(this.winSym, this)
            this.winSym.zIndex = (this.winSym.position.x / 720) * 10 + (this.winSym.position.y / 720) * 5;
            if (this.multiplier) {
                RoadRage.overlayAnim.addChild(this.multiplier);
                this.multiplier.position = Helper.copyLocalPosition(this.winSym, this)
                this.multiplier.position.x += this.multPos.x;
                this.multiplier.position.y += this.multPos.y;
            }
            this.winSym.parent.sortChildren();
        }
        return !!this.winSym;
    }

    stopWin(duration: number, ignoreDarknessChange?: boolean) {
        if (this.isScatter) return;
        if (this.useWinSymbol(false)) {
            // if (this.multiplier) {
            //     this.addChild(this.multiplier);
            //     this.multiplier.position = this.multPos;
            // }
            this.winSym.Stop(duration);
        }

        if (!ignoreDarknessChange)
            this.markNonWin(false, undefined, duration);
    }

    private definedCarColor(carCode: number): number {
        switch (carCode) {
            case 1:
                return SymbolGfxColor.RED;
            case 2:
                return SymbolGfxColor.GREEN;
            default:
                return SymbolGfxColor.GOLD;
        };
    }

    changeMysteryValue(toType: string): gsap.core.Timeline {

        this.visible = false;

        const tl = RoadRage.overlayAnim.playSequence(this, RoadRage.assetLoader.getTextures("M_" + this.sizeType.value + "_Reveal")).timeline.add(() => {
            this.playFlash()
            this.changeState({ forceState: true, state: toType[3], duration: 0, sym: "M" });
            this.visible = true;
        })
        SoundManager.playSoundEffect(SoundConfig.MYSTERY_REVEAL);

        return tl;
    }

    mysteryHits(hits: boolean): void {
        RoadRage.overlayAnim.playPlusOneText(this, 1, hits ? this.symName[2] : undefined, !hits).eventCallback("onComplete", () => this.playFlash());
    }

    get carColor(): number {
        return this.definedCarColor(parseFloat(this.symName.split("WC")[1]));
    }

    PlayGlowStop(): void {
        this._mainSp.destroy()
        this._subSp.destroy();

        this._mainSp = this.addChild(new PIXI.Sprite());
        this._mainSp.anchor.set(0.5, 0.5);
        this._subSp = this.addChild(new PIXI.Sprite());
        this._subSp.anchor.set(0.5, 0.5);
        this._subSp.alpha = 0;

        this.updateGFX(this.flashGFX, this.carColor);
        this.flashGFX.blendMode = BLEND_MODES.ADD;

        this._subTimeline = this.fetchSubTimeline({ repeat: -1, yoyo: true }, true);
        this._subTimeline.fromTo(this.flashGFX, { alpha: 0.25 }, { alpha: 0.5, duration: 1 });
    }

    public carMovesIn(fromRight: boolean, durr: number): gsap.core.Timeline {
        this._subTimeline = this.fetchSubTimeline();
        const texture: PIXI.Texture = RoadRageSymbolData.getTextures(this.symName, this.state.value, this.sizeType.value)![0];

        this._subSp.texture = this._mainSp.texture = texture;

        this._subTimeline.to(this.flashGFX, { alpha: 0, duration: durr }, 0);

        this._subTimeline.fromTo(this.mainSp, { alpha: 0, x: this.symbolWidht * (fromRight ? 2 : -2) }, { alpha: 1, x: 0, duration: durr }, 0);

        return this._subTimeline;
    }

    public carMovesOut(fromRight: boolean, durr: number): gsap.core.Timeline {
        this._subTimeline = this.fetchSubTimeline({
            onComplete: () => {
                this.CarOverlayDisp.destroy();
                this.CarOverlayDisp = null!;
                this.remove();
            }
        });
        const startPos: PIXI.Point = this.CarOverlayDisp.position;

        this._subTimeline.to(this.CarOverlayDisp, { alpha: 0, x: startPos.x + this.symbolWidht * (fromRight ? -2 : 2), duration: durr }, 0);

        return this._subTimeline;
    }

    skipped(): void {
        this._timeLine?.totalProgress(1);
        this._subTimeline?.totalProgress(1);
    }

    playBird(val:number = 1): gsap.core.Timeline {
        this.mainSp.visible = false;
        this.subSp.visible = false;

        const spine = RoadRage.overlayAnim.addChild(new SpinePlayer("F5", false));
        spine.position = Helper.copyLocalPosition(spine, this);

        spine.play({
            animation: "F5_" + this.sizeType.value + (val === 1 ? "" : "_2"), showOnEnd: true, onComplete: () => {
                this.addChild(spine);
                spine.position.set(0);
            }
        });

        if(this.multiplier){
            this.multiplier.destroy();
            this.multiplier = undefined!;
        } 

        return spine.timeLine;
    }

    MultSign(val:number = 1): gsap.core.Timeline {
        this.mainSp.visible = false;
        this.subSp.visible = false;

        const spine = RoadRage.overlayAnim.addChild(new SpinePlayer("F6", false));
        spine.position = Helper.copyLocalPosition(spine, this);

        spine.play({
            animation: "F6_" + this.sizeType.value + (val === 1 ? "" : "_2"), showOnEnd: true, onComplete: () => {
                this.addChild(spine);
                spine.position.set(0);
            }
        });

        if(this.multiplier){
            this.multiplier.destroy();
            this.multiplier = undefined!;
        } 

        return spine.timeLine;
    }

    RoadBump(val:number = 1): gsap.core.Timeline {
        this.mainSp.visible = false;
        this.subSp.visible = false;

        const spine = RoadRage.overlayAnim.addChild(new SpinePlayer("F7", false));
        spine.position = Helper.copyLocalPosition(spine, this);

        spine.play({
            animation: "F7_" + this.sizeType.value + (val === 1 ? "" : "_2"), showOnEnd: true, onComplete: () => {
                this.addChild(spine);
                spine.position.set(0);
            }
        });

        if(this.multiplier){
            this.multiplier.destroy();
            this.multiplier = undefined!;
        } 

        return spine.timeLine;
    }

    PlayIdle(duration: number): void {
        if (this.isScatter) return;
        if (this.useWinSymbol()) this.winSym.Idle(duration, this.isCar ? this.mainSp.texture : RoadRageSymbolData.getTextures(this.symName, "idle", this.sizeType.value)![0]);
    }

    setupMode(switchLandDirr: number, fromRight: boolean) {
        if (this.symName === "F2") {
            if (!this.posID[0] || (this.posID[0] < switchLandDirr)) this.scale.y = -1;
            this.scale.x = fromRight ? 1 : -1;
        }

        if (this.symName === "F3") {
            this.scale.x = fromRight ? 1 : -1;
        }

        if (this.isCar) {
            this.mainSp.scale.set(1);
            this.scale.set(fromRight ? 0.85 : -0.85, 0.85)
        }
    }

    public putCarOnOverlay(trail: boolean = false): void {

        this.CarOverlayDisp = RoadRage.overlayAnim.addChild(this.missingtSymType ? this.missingtSymType : this.mainSp);

        this._mainSp = this.addChild(new PIXI.Sprite());
        this._mainSp.anchor.set(0.5, 0.5);

        this.missingtSymType = undefined!;

        this.CarOverlayDisp.position = Helper.copyLocalPosition(this.CarOverlayDisp, this);
        this.CarOverlayDisp.scale.x = this.scale.x;
        this.CarOverlayDisp.scale.y = this.scale.y;
        this.CarOverlayDisp.zIndex = 1000;

        if (trail) this.createTrail();
    }

    private createTrail(): void {
        if (!this.CarOverlayDisp || this.CarOverlayTrail) return;

        if (this.symName === "WC1")
            this.CarOverlayTrail = RoadRage.overlayAnim.addChild(new PIXI.Sprite(RoadRage.assetLoader.getTextures("Cars_Trails/WC1_Trail")[0]));
        else
            this.CarOverlayTrail = RoadRage.overlayAnim.addChild(new PIXI.Sprite(RoadRage.assetLoader.getTextures("Cars_Trails/WC2_Trail")[0]));

        (this.CarOverlayTrail as PIXI.Sprite).anchor.set(this.symName === "WC1" ? 0 : 0.4, 0.5);
        this.CarOverlayTrail.position = this.CarOverlayDisp.position;
        this.CarOverlayTrail.scale = this.CarOverlayDisp.scale;
        this.CarOverlayTrail.zIndex = this.CarOverlayDisp.zIndex - 1;
        this.CarOverlayTrail.alpha = 0;
        RoadRage.overlayAnim.sortChildren();
    }

    public setupMark(colorType: string, duration: number): gsap.core.Tween | undefined {
        this.updateGFX(this._darkSquareGfx, this.definedCarColor(parseFloat(colorType.split("WC")[1])));
        this.markNonWin(true, 0.3, duration);
        this.isSetupMarked = true;
        return gsap.to(this, { alpha: 1, duration: duration });
    }

    resetSum(): void {
        this.nrOnMult = 1;
        if (this.multiplier) {
            this.multiplier.destroy();
            this.multiplier = undefined!;
        }
    }

    resetCarPos(): void {
        this.CarOverlayDisp.position.set(0, 0);

        this.addChild(this.CarOverlayDisp);
        this._mainSp = this.CarOverlayDisp as PIXI.Sprite;

        this.CarOverlayDisp = undefined!;
    }

    public CarSumMove(dist: number = 0, durrSpeed: number = 0.25): gsap.core.Timeline {
        this._timeLine = new gsap.core.Timeline();

        const delay: number = 0.25;
        const startScaleX: number = this.symName === "WC2" ? -1 : 1;
        this._timeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: delay }, 0)
        this._timeLine.fromTo(this.scale, { x: 0, y: 0 }, { x: startScaleX, y: 1, duration: delay, ease: Bounce.easeIn }, 0)
        // this._timeLine.add(() => { RoadRageReelOverlay.instance.flashCarDirection(this.symName, this, 0.5, 0) } ,delay)

        if (!dist) {
            return this._timeLine;
        }

        const startPos: PIXI.Point = MathHelper.pointAdd(this.position, 0);

        const shrinkDirectionX: number = (this.symbolWidht * startScaleX) / 6;

        const distDuration: number = durrSpeed + (1 - Power3.easeOut(1 / Math.abs(dist))) * (3 * durrSpeed);

        this._timeLine.to(this, { x: startPos.x + shrinkDirectionX, duration: durrSpeed, ease: Back.easeIn }, delay);
        this._timeLine.to(this.scale, { x: startScaleX * 0.85, duration: durrSpeed, ease: Back.easeIn }, delay);

        this._timeLine.to(this, { x: startPos.x + -(startScaleX * this.symbolWidht) * Math.abs(dist), duration: distDuration, ease: Power3.easeOut }, delay + durrSpeed)
        this._timeLine.to(this.scale, { x: startScaleX, duration: distDuration, ease: Power3.easeOut }, delay + durrSpeed)

        return this._timeLine;
    }

    private spineWild:SpinePlayer;
    public CarMoveTo(dist: number = 0, durrSpeed: number = 0.25): gsap.core.Timeline {
        this._subTimeline = this.fetchSubTimeline(undefined, true);

        if (!dist) return this._subTimeline;

        if (!this.CarOverlayDisp) this.putCarOnOverlay();

        this.createTrail();

        const startScaleX: number = this.symName === "WC2" ? -1 : 1;

        const startPos: PIXI.Point = this.CarOverlayDisp.position;

        const shrinkDirectionX: number = (this.symbolWidht * startScaleX) / 6;

        const startScale = MathHelper.pointAdd(this.CarOverlayDisp.scale, 0);

        const distDuration: number = durrSpeed + (1 - Power3.easeOut(1 / Math.abs(dist))) * (3 * durrSpeed);

        this._subTimeline.to([this.CarOverlayTrail, this.CarOverlayDisp], { x: startPos.x + shrinkDirectionX, duration: durrSpeed, ease: Back.easeIn }, 0);
        this._subTimeline.to([this.CarOverlayTrail.scale, this.CarOverlayDisp.scale], { x: startScale.x * 0.85, duration: durrSpeed, ease: Back.easeIn }, 0);

        this._subTimeline.add(() => {
            this.mainSp.visible = false;
            if (this.missingTexture)
                this.addMissingText("W");
            else {
                const tl: gsap.core.Timeline = this.changeState({ state: ReelSymbolStates.NORMAL, forceState: true, sym: "W" });
                if (this.mainSp) {
                    tl.add(() => { this.scale.set(1, 1) });
                    tl.fromTo(this.mainSp, { alpha: 0 }, { duration: 0.15, alpha: 1 });
                }
            }
        }, durrSpeed)

        this._subTimeline.add(() => {
            this.spineWild = this.addChild(new SpinePlayer("Wild_Reveal", true));
            this.spineWild.play({
                animation: "W_Reveal_" + this.sizeType.value,
                duration: SpinePlayer.fetchDuration("Wild_Reveal", "W_Reveal_" + this.sizeType.value),
                onComplete: () => { this.mainSp.visible = true, this.spineWild.destroy() }
            });
        }, durrSpeed + 0.05)

        this._subTimeline.fromTo(this.CarOverlayTrail, { alpha: 0 }, { alpha: Math.min(Math.abs(dist) * 0.3, 1), duration: distDuration / 4 }, durrSpeed);
        this._subTimeline.to(this.CarOverlayTrail, { alpha: 0, duration: distDuration / 4 }, durrSpeed + distDuration / 4);

        this._subTimeline.to([this.CarOverlayTrail, this.CarOverlayDisp], { x: startPos.x + -(this.symbolWidht * startScaleX) * Math.abs(dist), duration: distDuration / 2, ease: Power3.easeOut }, durrSpeed)
        this._subTimeline.to([this.CarOverlayTrail.scale, this.CarOverlayDisp.scale], { x: startScale.x, duration: distDuration / 2, ease: Power3.easeOut }, durrSpeed)

        return this._subTimeline;
    }

    public moveSideWays(toY: number): gsap.core.Timeline {
        this._subTimeline = this.fetchSubTimeline();

        if (!this.CarOverlayDisp) this.putCarOnOverlay();

        const startPos: PIXI.Point = this.CarOverlayDisp.position;

        const shrinkDirectionY: number = ((toY > 0 ? -1 : 1) * -this.symbolHeight / 4)

        const distDuration: number = 0.25 + (1 - Power3.easeOut(1 / Math.abs(toY))) * (3 * 0.25);

        this._subTimeline.to(this.CarOverlayDisp, { y: startPos.y + shrinkDirectionY, duration: 0.25, ease: Back.easeIn }, 0);
        this._subTimeline.to(this.CarOverlayDisp.scale, { y: 0.75, duration: 0.25, ease: Back.easeIn }, 0);

        this._subTimeline.to(this.CarOverlayDisp, { y: startPos.y + this.symbolHeight * toY, duration: distDuration, ease: Power3.easeOut }, 0.25)
        this._subTimeline.to(this.CarOverlayDisp.scale, { y: 1, duration: distDuration, ease: Power3.easeOut }, 0.25)

        return this._subTimeline;
    }

    public SetupMovement(dist: number = 0, collides: boolean = false): gsap.core.Timeline {
        this._subTimeline = this.fetchSubTimeline();

        if (!this.CarOverlayDisp) this.putCarOnOverlay();
        if (!dist) return this._subTimeline;

        const durrSpeed: number = 0.25;

        const startPos: PIXI.Point = this.CarOverlayDisp.position;

        const shrinkDirectionX: number = ((dist > 0 ? 1 : -1) * -this.symbolWidht / 6);

        const startScale = MathHelper.pointAdd(this.CarOverlayDisp.scale, 0);

        const distDuration: number = durrSpeed + (1 - Power3.easeOut(1 / Math.abs(dist))) * (3 * durrSpeed);

        this._subTimeline.to(this.CarOverlayDisp, { x: startPos.x + shrinkDirectionX, duration: durrSpeed, ease: Back.easeIn }, 0);
        this._subTimeline.to(this.CarOverlayDisp.scale, { x: startScale.x * 0.85, duration: durrSpeed, ease: Back.easeIn }, 0);

        this._subTimeline.to(this.CarOverlayDisp, { x: startPos.x + this.symbolWidht * dist, duration: distDuration / 2, ease: Power3.easeOut }, durrSpeed)
        this._subTimeline.to(this.CarOverlayDisp.scale, { x: startScale.x, duration: distDuration / 2, ease: Power3.easeOut }, durrSpeed)

        return this._subTimeline;
    }

    public get overlayCar(): PIXI.DisplayObject {
        return this.CarOverlayDisp;
    }

    public changeConstruction(type: string, durr: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = this.changeState({ state: ReelSymbolStates.NORMAL, forceState: true, sym: type, duration: durr })

        this.applyMultValue(2);

        if (this.multiplier) {
            tl.fromTo(this.multiplier.scale, { x: 3, y: 3 }, { x: 1, y: 1, ease: Bounce.easeOut, duration: durr }, 0);
            tl.fromTo(this.multiplier, { alpha: 0 }, { alpha: 1, duration: durr }, 0);
            tl.add(() =>{ SoundManager.playSoundEffect(SoundConfig.MULT_LAND_UPGRADE)}, durr-0.1)
        }

        return tl;
    }

    public onBounceEffect(landDurr?: number, isSticky: boolean = false): void {

        if (this.isCar) {
            this.putCarOnOverlay();
            this.overlayCar.scale = this.scale;
            const toScale: PIXI.Point = MathHelper.pointMul(this.scale, 1 / this.scale.y);
            this.scale = toScale;
            gsap.to(this.overlayCar.scale, { x: toScale.x, y: toScale.y, duration: 0.25, ease: Bounce.easeOut });
            gsap.to(this, { duration: landDurr }).eventCallback("onUpdate", () => {
                if (this.CarOverlayDisp && this.CarOverlayDisp.parent)
                    this.CarOverlayDisp.position = Helper.copyLocalPosition(this.CarOverlayDisp, this)
            });
            SoundManager.playSoundEffect(this.symName === "WC3" ? SoundConfig.CAR_LAND_YELLOW : SoundConfig.CAR_LAND_GREEN_AND_RED);
        }
       
    }

    playBounceMystery():gsap.core.Timeline{
        this.mainSp.visible = false;
        this.subSp.visible = false;
        SoundManager.playSoundEffect(SoundConfig.MYSTERY_LAND);
        return RoadRage.overlayAnim.playSpine(this,"M_Landing",{ animation:"M_Landing_" + this.sizeType.value,onComplete:() =>  
        {
            this.mainSp.visible = true;
            this.subSp.visible = true;
        }}).timeline;
    }

    public hideBehindSticky(landDurr?: number): void {
        gsap.to(this, { alpha: 0, duration: landDurr }).eventCallback("onComplete", () => { this.remove() });
    }

    public changeCarToWild(collided: boolean, isSticky: boolean = false): void {
        if (isSticky) {
            this.changeState({ state: "Sticky", forceState: true, duration: 0 })
            this.markNonWin(false, 0, 0.1);
        }

        if (!this.CarOverlayDisp) this.putCarOnOverlay();
        this.CarOverlayDisp.destroy();
    }

    public SwearSetup() {
        const anim: string = ArrayHelper.getRandomValue("Speech_Bitch,Speech_Fuck,Speech_Shit".split(","));
        const swearOverlay = RoadRage.overlayAnim.playSpine(this.overlayCar, "Speech", { animation: anim });
        swearOverlay.item.zIndex = this.overlayCar.zIndex + 1;
        swearOverlay.item.pivot.set(0, 55);
        swearOverlay.item.parent.sortChildren();
        SoundManager.playSoundEffect(SoundConfig.swearSound(anim),undefined,0.75);
    }

    private swearItem: OverlayItem;
    public Swear() {
        let anim: string = ArrayHelper.getRandomValue(["Speech_Bitch", "Speech_Fuck", "Speech_Shit"]);
        const pivXOff: number = anim === "Speech_Fuck" ? -12 : 0;

        SoundManager.playSoundEffect(SoundConfig.swearSound(anim),undefined,0.75);

        anim += Math.random() > 0.5 ? "_Flipped" : "";

        this.swearItem = RoadRage.overlayAnim.playSpine(this, "Speech", { animation: anim });
        this.swearItem.item.pivot.set(pivXOff + fetchSpineX(this.symName), fetchSpiney(this.symName));

        function fetchSpiney(name: string): number {
            switch (name) {
                case "M1": // Red
                    return 70;
                case "M2": // Yellow
                    return 55;
                case "M3": // Green
                    return 55;
                case "M4": // Purp
                    return 55;
                case "M5": // Blue
                    return 55;
                default:
                    return 65;
            }
        }
        function fetchSpineX(name: string): number {
            switch (name) {
                case "M1": // Red
                    return -10;
                case "M2": // Yellow
                    return -30;
                case "M3": // Green
                    return -50;
                case "M4": // Purp
                    return -20;
                case "M5": // Blue
                    return -20;
                default:
                    return -40;
            }
        }
    }

    public Explosion() {
        const exp = RoadRage.overlayAnim.playSequence(this,RoadRage.assetLoader.getTextures("FX_StickySplash")).item;
        exp.zIndex = 9001;
        exp.scale.set(0.75);
        exp.parent.sortChildren();
    }

    public applyMultValue(value: number = 1, yPos: number = this.symbolHeight / 4, spec?: boolean) {
        if (value == 1 || !this.visible) return;
        if (!this.multiplier) {
            this.multiplier = new RoadRageSymbolMultiplier(value, this.symbolHeight);
            this.multiplier.visible = this.visible;
            this.multiplier.zIndex = 250;

            RoadRage.overlayAnim.addChild(this.multiplier);
            this.multiplier.position = Helper.copyLocalPosition(this.multiplier, this);
            this.multiplier.position.x += this.multPos.x;
            this.multiplier.position.y += this.multPos.y;
        }
        if (this.children.find(v => v === this.multiplier!)) {
            this.multiplier.position = this.multPos;
        }

        if (spec) {
            this.multiplier.scale.x = this.scale.x;
            this.multiplier.pivot.set(66,10);
        }

        this.nrOnMult = value;
        this.multiplier.setValue(value);
    }

    public mergeSymbolMult():void{
        if(this.multiplier){
            this.addChild(this.multiplier).sortChildren();
            this.multiplier.position = this.multPos;
        }
    }

    public onSticky(): void {

        if (this.multiplier) {
            this.addChild(this.multiplier).sortChildren();
            this.multiplier.position = this.multPos;
        }

        if (!this.state.equal("Sticky") && this.isCar){

            const animationName: string = "Pop_WC_" 
            + this.symName.slice(2)
            + "_" + this.sizeType.value + "_Indicator";

            if (this.multiplier){
                RoadRage.overlayAnim.addChild(this.multiplier);
                this.multiplier.position = Helper.copyLocalPosition(this.multiplier, this);
                this.multiplier.position.x += this.multPos.x;
                this.multiplier.position.y += this.multPos.y;
            }
            
            RoadRage.overlayAnim.playSpine(this, "Wild_Indicator", {
                animation: animationName, onStart: () => this.visible = true, onComplete: () => {
                    if (this.multiplier) {
                        this.addChild(this.multiplier).sortChildren();
                        this.multiplier.position = this.multPos;
                    }
                }
            }, false);

            this.changeState({ forceState: true, state: "Sticky", duration: 0.1 });

            if(this.winSym){
                this.winSym.remove();
                this.winSym = undefined!;
            } 
        }

        this.scale.set(1, 1);
    }

    public setVisible(value: boolean): void {
        super.setVisible(value);
        if (this.winSym) this.winSym.visible = value;
        if (this.multiplier) this.multiplier.visible = value;
    }

    playFlash(durr: number = 0.25) {
        // this._subTimeline = this.fetchSubTimeline(undefined, true);
        gsap.fromTo(this.flashGFX, { alpha: 1 }, { duration: durr, alpha: 0 });
    }

    protected updateGraphics(): void {
        this.updateGFX(this._darkSquareGfx);
        this.updateGFX(this.flashGFX, 0xFFFFFF);
    }

    private updateGFX(target: PIXI.Graphics, color: number = 0x000000) {
        if (!target.parent) return;

        const w: number = this.symbolWidht;
        const h: number = this.symbolSizeY;

        target.scale.set(1, 1);
        target.clear();
        target.beginFill(color, 1);
        target.drawRect(-w * 0.5, -h * 0.5, w, h);
        target.endFill();
        target.blendMode = BLEND_MODES.NORMAL;
    }

    protected setSymbolState(config: ISymbolAnimationConf): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline({
            onComplete: () => {
                if (this.subSp && this.mainSp && this.parent) {
                    this.subSp.scale = this.mainSp.scale;
                    this.subSp.texture = this.mainSp.texture;
                }
            }
        }), true);

        const toConf: ISymbolConfig = config.sym ? SymbolData.getConfig(config.sym!) : this.symbolConfig;
        const toSizeType: any = config.sizeType && this.sizeType.value != config.sizeType ? config.sizeType! : this.sizeType.value;
        const toState: any = config.state && this.state.value != config.state ? config.state! : this.state.value;

        const TEXTURE_FROM: PIXI.Texture[] | undefined = SymbolData.getTextures(this.symbolConfig.NAME, this.state.value, this.sizeType.value);
        const TEXTURES_TO: PIXI.Texture[] | undefined = SymbolData.getTextures(toConf.NAME, toState, toSizeType);

        if (this.missingTexture) {
            this.state.set(toState);
            this.sizeType.set(toSizeType);
            this.symbolConfig = toConf;
            return this._timeLine;
        }

        this.state.set(toState);

        this.expandAnimation(toSizeType, config.duration ? config.duration : 0, config.ease);

        this.sizeType.set(toSizeType);

        this.symbolConfig = toConf;

        this.textureAnimation(TEXTURE_FROM, TEXTURES_TO, config.duration ? config.duration : 0, config.ease ? config.ease : Power4.easeInOut);

        return this._timeLine;
    }

    protected expandAnimation(size: number, _duration: number, _ease: any): void {
        if (!this.sizeType.equal(size)) {

            const currSize: number = RoadRageSymbolData.fetchSizeTimesSize(this.sizeType.value);
            const expectedSize: number = RoadRageSymbolData.fetchSizeTimesSize(size);

            const toScale: PIXI.Point = new PIXI.Point(1, expectedSize / currSize);
            const fromScale: PIXI.Point = new PIXI.Point(1, currSize / expectedSize);

            // Texture
            this._timeLine!.fromTo(this.mainSp.scale, { x: fromScale.x, y: fromScale.y }, { x: 1, y: 1, duration: _duration, ease: _ease }, 0)
            this._timeLine!.fromTo(this.subSp.scale, { x: 1, y: 1 }, { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease }, 0);

            // GFX
            this._timeLine!.fromTo(this._darkSquareGfx.scale, { x: 1, y: 1 }, { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease }, 0);
            this._timeLine!.fromTo(this.flashGFX.scale, { x: 1, y: 1 }, { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease }, 0);

            this._timeLine!.add(() => this.updateGraphics());
            this.sizeType.set(size);
        }
    }

    protected textureAnimation(_TextureFrom: PIXI.Texture[] | undefined, _TextureTo: PIXI.Texture[] | undefined, _duration: number, _ease?: any): void {
        if (_TextureTo) {

            this.mainSp!.texture = _TextureTo[0];
            this._timeLine!.fromTo(this.mainSp, { alpha: 0 }, { duration: _duration, alpha: 1, ease: _ease }, 0)

            if (_TextureFrom) {
                this.subSp!.texture = _TextureFrom[0];
                this._timeLine!.fromTo(this.subSp, { alpha: 1 }, { duration: _duration, alpha: 0, ease: _ease }, 0);
            }
        }
    }

    remove(): void {
        if (this.winSym) {
            this.winSym.remove();
            this.winSym = null!;
        }
        if (this.CarOverlayDisp && this.CarOverlayDisp.parent) {
            this.CarOverlayDisp.destroy();
            this.CarOverlayDisp = undefined!;
        }

        if (this.multiplier && this.multiplier.parent) {
            this.multiplier.remove();
            this.multiplier = undefined!;
        }

     

        super.remove();
    }
}
